import {
  Stack,
  styled,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "~/common/mui";
import * as PriceUtils from "~/utils/PriceUtils";
import { DateFormatType } from "~/common/utils/DateFormatter";
import { useTranslation } from "react-i18next";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import type { MMDTableData } from "~/features/productDetails/components/MMDAssistant/MMDAssistant.types";
import { MMDCompareProductsButton } from "~/features/productDetails/components/MMDAssistant/components/MMDCompareProducts";
import { uniqueValues } from "~/common/utils/misc";
import { InfoCircleV2 } from "~/common/components/InfoCircle/InfoCircleV2";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";

interface Props extends MMDTableData {
  loading?: boolean;
}

const MMDResultsTable = ({ items = [], totalNetValue, totalQuantity, totalDifferentProducts }: Props) => {
  const { t } = useTranslation();

  const showCompareButton = totalDifferentProducts > 1 && totalDifferentProducts <= 5;
  const productCodes = uniqueValues(items.map((item) => item.productCode));

  return (
    <Table>
      <TableHead>
        <TableRow>
          <HeaderCell sx={{ width: "10%" }}>
            <Typography variant="labelMedium">
              {window.SCHA.config.showESD ? t("feature.mmd.assistant.table.esd") : t("feature.mmd.assistant.table.ead")}
            </Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "12%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.product")}</Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "16%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.materialNo")}</Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "16%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.packaging")}</Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "12%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.alias")}</Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "10%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.quantity")}</Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "10%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.netPrice")}</Typography>
          </HeaderCell>
          <HeaderCell sx={{ width: "12%" }}>
            <Typography variant="labelMedium">{t("feature.mmd.assistant.table.netValue")}</Typography>
          </HeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, index) => (
          <TableRow key={index}>
            <StyledCell>{formatShippingOrDeliveryDate(item.esd, DateFormatType.DATE)}</StyledCell>
            <StyledCell>{item.designation}</StyledCell>
            <StyledCell>{item.materialNo}</StyledCell>
            <StyledCell>
              {t("addToCartPopup.packSize", {
                amount: NF.getDefaultFormatter().format(item.packSize),
                code: item.packCode,
              })}
            </StyledCell>
            <StyledCell>{item.customerMaterialNo || "-"}</StyledCell>
            <StyledCell>
              <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                {item.quantity}
                {!item.esd && (
                  <InfoCircleV2
                    iconSize="small"
                    placement="bottom"
                    content={t("feature.mmd.assistant.table.replenishmentPopup", { count: item.quantity })}
                  />
                )}
              </Stack>
            </StyledCell>
            <StyledCell>{PriceUtils.text(item.netPrice)}</StyledCell>
            <StyledCell>{PriceUtils.text(item.netValue)}</StyledCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <StyledCell colSpan={8} align="center">
              <Typography variant="labelMedium" sx={{ py: 2 }}>
                {t("feature.mmd.assistant.table.empty")}
              </Typography>
            </StyledCell>
          </TableRow>
        )}
      </TableBody>
      <StyledTableFooter>
        <TableRow>
          <StyledCell colSpan={1} />
          <StyledCell colSpan={4}>
            <Stack gap={1}>
              <Typography variant="labelMedium">
                {t("feature.mmd.assistant.dialog.material", { count: totalDifferentProducts })}
              </Typography>
              {showCompareButton && <MMDCompareProductsButton products={productCodes} />}
            </Stack>
          </StyledCell>
          <StyledCell>
            <Typography variant="labelMedium">{totalQuantity}</Typography>
          </StyledCell>
          <StyledCell></StyledCell>
          <StyledCell>
            <Stack gap={1}>
              <Typography variant="labelMedium" sx={{ fontWeight: "bold" }}>
                {PriceUtils.text(totalNetValue)}
              </Typography>
              <Typography variant="labelMedium">{t("feature.mmd.assistant.table.total")}</Typography>
            </Stack>
          </StyledCell>
        </TableRow>
      </StyledTableFooter>
    </Table>
  );
};

const StyledTableFooter = styled(TableFooter)({
  // Last row should have a thick 3px border
  "& tr:first-child > td": {
    borderTop: `3px solid ${C_OUTLINE_VARIANT}`,
  },
});

const StyledCell = styled(MuiTableCell)({
  padding: "8px",
});

const HeaderCell = styled(StyledCell)({
  "> label": {
    fontWeight: "bold",
  },
});

export default MMDResultsTable;
